// ProcessDiagramEdit.js
import { useState } from 'react'
import {
    PencilIcon,
    PaletteIcon,
    FolderOpenIcon,
    TrashIcon,
    SparkleIcon,
} from '@phosphor-icons/react'
import { useDispatch, useSelector } from 'react-redux'
import { deleteProcessDiagram } from '../../../store/actions'
import { ROUTES } from '../../../router/routes'
import { useNavigate } from 'react-router-dom'
import { useModal, MODALS } from '../../modals'
import { loadFromSavedProcessDiagram } from './ProcessDiagramAPI'
import { ScoopMenu, ScoopMenuItem, ScoopMenuDivider, ScoopButton } from '../../ui'
import styles from './ProcessDiagramEdit.module.scss'

export default function ProcessDiagramEdit({
    setIsMenuOpen,
    isProcessDiagramASkeleton,
    setIsProcessDiagramASkeleton,
    title,
    id,
    processDiagram,
    handleTitleChange,
    workspaceMetadata,
}) {
    const [anchorEl, setAnchorEl] = useState(null)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const editElementModal = useModal(MODALS.CANVAS.EDIT_ELEMENT)
    const deleteModal = useModal(MODALS.COMMON.DELETE)
    const loadDiagramModal = useModal(MODALS.PROCESS.LOAD_DIAGRAM)

    const open = Boolean(anchorEl)

    const handleClick = (event) => {
        setIsMenuOpen(true)
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setIsMenuOpen(false)
        setAnchorEl(null)
    }

    const createMenuItemHandler = (handler) => (event) => {
        event.stopPropagation()
        handler()
        setAnchorEl(null)
        setIsMenuOpen(false)
    }

    const handleDelete = () => {
        dispatch(deleteProcessDiagram(id))
        setIsMenuOpen(false)
        setAnchorEl(null)
    }

    const handleOpenLoadDiagram = async () => {
        const result = await loadDiagramModal.show()
        if (result?.diagramName) {
            handleTitleChange({
                target: { value: result.diagramName },
            })
            loadFromSavedProcessDiagram(
                result.diagramName,
                setIsProcessDiagramASkeleton,
                dispatch,
                id,
                workspaceID
            )
        }
        setIsMenuOpen(false)
    }

    return (
        <>
            <div>
                <ScoopButton
                    variant="text"
                    size="xxs"
                    onClick={handleClick}
                    aria-label="Open menu"
                    className={styles.menuButton}
                >
                    <SparkleIcon size={24} weight="fill" className={styles.sparkleIcon} />
                </ScoopButton>
                <ScoopMenu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    {!isProcessDiagramASkeleton && (
                        <ScoopMenuItem
                            leftSection={<PencilIcon size={18} weight="regular" />}
                            primary="Edit"
                            onClick={createMenuItemHandler(() =>
                                navigate(ROUTES.INSIGHTS, {
                                    state: {
                                        selectedTab: 'processes',
                                        processDiagramToEdit: processDiagram,
                                        workspaceMetadata,
                                    },
                                })
                            )}
                        />
                    )}
                    <ScoopMenuItem
                        leftSection={<PaletteIcon size={18} weight="regular" />}
                        primary="Style"
                        onClick={createMenuItemHandler(() =>
                            editElementModal.show({ objectId: id })
                        )}
                    />
                    <ScoopMenuItem
                        leftSection={<FolderOpenIcon size={18} weight="regular" />}
                        primary="Load"
                        onClick={createMenuItemHandler(handleOpenLoadDiagram)}
                    />
                    <ScoopMenuDivider />
                    <ScoopMenuItem
                        leftSection={<TrashIcon size={18} weight="regular" />}
                        primary="Delete"
                        onClick={createMenuItemHandler(() =>
                            deleteModal.show({
                                title,
                                type: 'Process Diagram',
                                onDelete: handleDelete,
                            })
                        )}
                    />
                </ScoopMenu>
            </div>
        </>
    )
}
